import React, {
  ComponentType,
  Dispatch,
  MutableRefObject,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { ExpandedState, RowData } from '@tanstack/react-table';
import cs from 'clsx';
import {
  ActionIcon,
  Checkbox,
  Flex,
  ScrollArea,
  UnstyledButton,
  CSSProperties,
  ScrollAreaProps,
  FlatTreeNode,
  flattenTreeData,
  useTree,
  rem,
  type RenderTreeNodePayload,
  type TreeNodeData,
} from '@mantine/core';
import IconChevronDown from '@tabler/icons-react/dist/esm/icons/IconChevronDown.mjs';
import {
  FloatingRowIndicators,
  type FloatingRowIndicatorsProps,
  useFloatingRowIndicator,
} from '@/shared/ui/floating-row-indicator';
import {
  collapse,
  collapseRecursive,
  expand,
  expandRecursive,
} from './helpers/expand-controls';
import { measureTreeNodeHeight } from './helpers/measure-node-height';
import classes from './tree.module.css';
import { TreeTestIdContext } from './test-id-context';
import { isEqual } from 'lodash';
import { useDeepCompareMemoize } from '@/shared/lib/hooks/use-deep-effect';

export interface TreeNode {
  value: string;
  label: React.ReactNode;
  children?: TreeNode[];
  selectable?: boolean;
  disabled?: boolean;
}

export type TreeNodeContext = {
  expand: (value: string) => void;
  collapse: (value: string) => void;
  toggleExpanded: () => void;
  expandRecursive: (value: string) => void;
  collapseRecursive: (value: string) => void;
};
export type ScrollControlRef<T extends RowData = RowData> = {
  scrollToIndex: (index: number) => void;
  scrollToItem: (cb: (item: T) => boolean) => void;
};
export interface TreeComponentProps<T extends RowData> {
  data: T[];
  valueField?: (node: T) => string;
  value?: string;
  selectable?: boolean;
  onChange?: (value: string | undefined) => void;
  state?: { expanded: Record<string, boolean> };
  initialState?: { expanded: Record<string, boolean> };
  onExpandedStateChange?: (expanded: Exclude<ExpandedState, boolean>) => void;
  onExpand?: (value: string) => void;
  checkable?: {
    checkStrictly?: boolean;
    checkedState: string[];
    setCheckedState: React.Dispatch<React.SetStateAction<string[]>>;
    position?: 'left' | 'right';
  };
  renderNode?: (node: T, context: TreeNodeContext) => React.ReactNode;
  scrollControlRef?: MutableRefObject<any>;
  classNames?: {
    root?: string;
    node?: {
      root?: string;
      chevron?: string;
      buttonWrapper?: string;
    };
    indicator?: {
      active?: string;
      hover?: string;
    };
  };
  indicators?: ComponentType<FloatingRowIndicatorsProps>;
  styles?: {
    root?: CSSProperties;
    node?: {
      root?: CSSProperties;
      chevron?: CSSProperties;
      buttonWrapper?: CSSProperties;
    };
  };
  levelOffset?: number;
  mah?: any;
  scrollAreaProps?: ScrollAreaProps;
}

const mapTreeData = <T extends TreeNode>(
  nodes: T[],
  valueField: (node: T) => string,
): TreeNodeData[] =>
  nodes.map((node) => ({
    ...node,
    value: valueField(node),
    children: node.children?.length
      ? mapTreeData(node.children as T[], valueField)
      : node.children,
  }));

type IndicatorEvents = Pick<
  ReturnType<typeof useFloatingRowIndicator>,
  'registerItem' | 'onItemHover'
>;

const IndicatorEventsContext = createContext<IndicatorEvents | null>(null);

const TreeIndicatorContainer = ({
  value,
  indicators: Indicators,
  indicatorClasses,
  style,
  children,
}: {
  value?: string;
  indicators?: ComponentType<FloatingRowIndicatorsProps>;
  indicatorClasses?: TreeComponentProps<TreeNode>['classNames']['indicator'];
  style: CSSProperties;
  children: React.ReactNode;
}) => {
  const testIdPrefix = useContext(TreeTestIdContext);
  const indicator = useFloatingRowIndicator({
    activeId: value,
    enabled: Boolean(Indicators),
  });
  const { registerItem, onItemHover } = indicator;
  const events = useMemo(
    () => (Indicators ? { registerItem, onItemHover } : null),
    [Indicators, registerItem, onItemHover],
  );

  return (
    <IndicatorEventsContext.Provider value={events}>
      <div
        ref={indicator.setParent}
        data-testid={
          testIdPrefix ? `${testIdPrefix}-list` : 'mantine-tree-component'
        }
        data-tree-root
        role='tree'
        style={style}
        onMouseLeave={indicator.onMouseLeave}
      >
        {Indicators && (
          <Indicators
            parent={indicator.parent}
            hoverTarget={indicator.hoverTarget}
            activeTarget={indicator.activeTarget}
            hoverClassName={indicatorClasses?.hover}
            activeClassName={indicatorClasses?.active}
            hoverTransitionDuration={indicator.hoverTransitionDuration}
            activeTransitionDuration={indicator.activeTransitionDuration}
            hoverTestId='tree-hover-indicator'
            activeTestId='tree-active-indicator'
          />
        )}
        {children}
      </div>
    </IndicatorEventsContext.Provider>
  );
};

export * from './helpers/expand-menu';
export * from './test-id-context';

const TreeRow = React.memo(
  <T extends TreeNode>({
    payload,
    value,
    selectable,
    onSelect,
    renderNode,
    checkable,
    classNames,
    styles,
    levelOffset,
    onExpand,
    setExpandedState,
    data,
  }: {
    payload: RenderTreeNodePayload;
    value?: string;
    selectable: boolean;
    onSelect: (nodeId: string) => void;
    renderNode?: (node: T, context: TreeNodeContext) => React.ReactNode;
    checkable?: TreeComponentProps<T>['checkable'];
    classNames?: TreeComponentProps<T>['classNames'];
    styles?: TreeComponentProps<T>['styles'];
    levelOffset: number;
    onExpand?: (value: string) => void;
    setExpandedState: Dispatch<SetStateAction<Record<string, boolean>>>;
    data: TreeNodeData[];
  }) => {
    const testIdPrefix = useContext(TreeTestIdContext);
    const events = useContext(IndicatorEventsContext);
    const registerNode = events?.registerItem;
    const onNodeHover = events?.onItemHover;
    const node = payload.node as T;
    const nodeId = node.value;
    const registerRef = useCallback(
      (element: HTMLElement | null) => registerNode?.(nodeId, element),
      [nodeId, registerNode],
    );
    const isSelectable = node.selectable !== false;
    const depth = payload.level - 1;
    const extraLeafOffset = !payload.hasChildren && depth > 0 ? levelOffset : 0;
    const isChecked = checkable
      ? checkable.checkedState.includes(nodeId)
      : undefined;

    const toggleCheck = () => {
      if (!checkable?.setCheckedState) return;
      if (checkable.checkedState.includes(nodeId)) {
        checkable.setCheckedState(
          checkable.checkedState.filter((id) => id !== nodeId),
        );
      } else {
        checkable.setCheckedState([...checkable.checkedState, nodeId]);
      }
    };

    const valueField = (item: TreeNodeData) => item.value;
    const checkboxPosition = checkable?.position ?? 'left';
    const checkboxOnLeft = checkboxPosition !== 'right';
    const checkbox =
      typeof isChecked !== 'undefined' ? (
        <span className={classes.checkboxSlot} data-position={checkboxPosition}>
          <Checkbox
            data-testid={
              testIdPrefix
                ? `${testIdPrefix}-checkbox-${nodeId}`
                : `checkbox-${nodeId}`
            }
            checked={isChecked}
            onChange={toggleCheck}
            onClick={(event) => event.stopPropagation()}
            tabIndex={-1}
          />
        </span>
      ) : null;

    return (
      <Flex
        ref={registerRef}
        data-testid={
          testIdPrefix ? `${testIdPrefix}-item-${nodeId}` : `node-${nodeId}`
        }
        align='center'
        gap={6}
        className={cs(
          '!cursor-default',
          'justify-start',
          'grow',
          classNames?.node?.root,
        )}
        w='100%'
        style={styles?.node?.root}
        data-active={!nodeId || nodeId === value ? 'true' : undefined}
        onMouseOver={() => onNodeHover?.(nodeId)}
      >
        {checkboxOnLeft && checkbox}

        <Flex
          align='center'
          gap={6}
          ml={depth * levelOffset + extraLeafOffset}
          className='min-w-0 grow overflow-hidden'
        >
          {payload.hasChildren && (
            <ActionIcon
              data-testid={
                testIdPrefix
                  ? `${testIdPrefix}-expand-${nodeId}`
                  : `chevron-${nodeId}`
              }
              size='sm'
              variant='subtle'
              color='gray'
              onClick={(event) => {
                event.stopPropagation();
                payload.tree.toggleExpanded(nodeId);
                onExpand?.(nodeId);
              }}
              className={classNames?.node?.chevron}
              tabIndex={-1}
            >
              <IconChevronDown
                size={14}
                style={{
                  transform: payload.expanded
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  ...styles?.node?.chevron,
                }}
              />
            </ActionIcon>
          )}

          <UnstyledButton
            onClick={selectable ? () => onSelect(nodeId) : undefined}
            className={cs(
              'grow overflow-hidden',
              classNames?.node?.buttonWrapper,
              {
                '!pointer-events-none': !isSelectable,
              },
            )}
            style={{ textAlign: 'left', ...styles?.node?.buttonWrapper }}
            data-testid={
              testIdPrefix
                ? `${testIdPrefix}-select-${nodeId}`
                : `render-wrapper-${nodeId}`
            }
          >
            {renderNode
              ? renderNode(node, {
                  collapse: collapse.bind(null, {
                    node: nodeId,
                    onExpand,
                    setExpandedState,
                  }),
                  expand: expand.bind(null, {
                    node: nodeId,
                    onExpand,
                    setExpandedState,
                  }),
                  toggleExpanded: () => payload.tree.toggleExpanded(nodeId),
                  expandRecursive: expandRecursive.bind(null, {
                    node: nodeId,
                    onExpand,
                    data,
                    setExpandedState,
                    valueField,
                  }),
                  collapseRecursive: collapseRecursive.bind(null, {
                    node: nodeId,
                    onExpand,
                    expandedState: payload.tree.expandedState,
                    data,
                    setExpandedState,
                    valueField,
                  }),
                })
              : node.label}
          </UnstyledButton>
        </Flex>
        {!checkboxOnLeft && checkbox}
      </Flex>
    );
  },
  (previous, next) => {
    const {
      payload: previousPayload,
      value: previousValue,
      ...previousProps
    } = previous;
    const { payload: nextPayload, value: nextValue, ...nextProps } = next;
    return (
      (previousValue === previousPayload.node.value) ===
        (nextValue === nextPayload.node.value) &&
      previousPayload.node === nextPayload.node &&
      previousPayload.level === nextPayload.level &&
      previousPayload.expanded === nextPayload.expanded &&
      previousPayload.hasChildren === nextPayload.hasChildren &&
      previousPayload.tree.expandedState === nextPayload.tree.expandedState &&
      Object.keys(previousProps).every((key) =>
        key === 'classNames' || key === 'styles'
          ? isEqual(previousProps[key], nextProps[key])
          : previousProps[key] === nextProps[key],
      )
    );
  },
);

const defaultValueField = (node: TreeNode) => node.value;
const flatNodeStyle = { width: '100%' };

// FlatTreeNode only reads selection for its own node. Selection helpers are
// unused here (selectOnClick is disabled); expansion/loading still invalidate it.
const MemoFlatTreeNode = React.memo(
  ({
    treeRef,
    selected,
    ...props
  }: Omit<React.ComponentProps<typeof FlatTreeNode>, 'tree'> & {
    treeRef: React.RefObject<ReturnType<typeof useTree>>;
    selected: boolean;
  }) => {
    const rowTree = useMemo(() => {
      const selectedState = selected ? [props.node.value] : [];
      // Stable identity per row; event handlers always read the latest controller.
      return new Proxy({} as ReturnType<typeof useTree>, {
        get: (_, key) =>
          key === 'selectedState' ? selectedState : treeRef.current[key],
      });
    }, [selected, props.node.value, treeRef]);
    return <FlatTreeNode {...props} tree={rowTree} />;
  },
);

export const TreeComponent = <T extends TreeNode>({
  data,
  valueField = defaultValueField,
  value,
  onChange,
  state,
  initialState,
  onExpandedStateChange,
  onExpand,
  checkable: checkableProp,
  renderNode,
  classNames: classNamesProp,
  styles: stylesProp,
  levelOffset = 28,
  selectable = true,
  mah,
  scrollAreaProps,
  scrollControlRef,
  indicators: IndicatorsProp,
}: TreeComponentProps<T>) => {
  const testIdPrefix = useContext(TreeTestIdContext);
  const checkable = useDeepCompareMemoize(checkableProp);
  const classNames = useDeepCompareMemoize(classNamesProp);
  const styles = useDeepCompareMemoize(stylesProp);
  const treeData = useMemo(
    () => mapTreeData(data, valueField),
    [data, valueField],
  );

  const selectedState = useMemo(() => (value ? [value] : []), [value]);

  const tree = useTree({
    expandedState: state?.expanded,
    initialExpandedState: initialState?.expanded,
    onExpandedStateChange,
    selectedState,
    checkStrictly: checkable?.checkStrictly ?? true,
  });
  const treeRef = useRef(tree);
  treeRef.current = tree;
  const selectionRef = useRef({ value, onChange });
  selectionRef.current = { value, onChange };

  const setExpandedState = useCallback<
    Dispatch<SetStateAction<Record<string, boolean>>>
  >((updater) => {
    const next =
      typeof updater === 'function'
        ? updater(treeRef.current.expandedState)
        : updater;
    treeRef.current.setExpandedState(next);
  }, []);

  const flatList = useMemo(
    () => flattenTreeData(treeData, tree.expandedState),
    [treeData, tree.expandedState],
  );

  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: flatList.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 44,
    overscan: 8,
    getItemKey: (index) => flatList[index]?.node.value ?? index,
    measureElement: measureTreeNodeHeight,
    useAnimationFrameWithResizeObserver: true,
  });

  useImperativeHandle<ScrollControlRef, ScrollControlRef>(
    scrollControlRef,
    () => ({
      scrollToIndex: (index) => {
        virtualizer.scrollToIndex(index);
      },
      scrollToItem: (cb) => {
        const index = flatList.findIndex((item) => cb(item.node as T));
        if (index !== -1) {
          virtualizer.scrollToIndex(index);
        }
      },
    }),
  );

  const handleSelect = useCallback((nodeId: string) => {
    const { value, onChange } = selectionRef.current;
    if (value === nodeId) onChange?.(undefined);
    else onChange?.(nodeId);
  }, []);

  const Indicators =
    IndicatorsProp ??
    (classNames?.indicator ? FloatingRowIndicators : undefined);

  const renderMantineNode = useCallback(
    (payload: RenderTreeNodePayload) => (
      <TreeRow
        payload={payload}
        value={payload.selected ? payload.node.value : undefined}
        selectable={selectable}
        onSelect={handleSelect}
        renderNode={renderNode}
        checkable={checkable}
        classNames={classNames}
        styles={styles}
        levelOffset={levelOffset}
        onExpand={onExpand}
        setExpandedState={setExpandedState}
        data={treeData}
      />
    ),
    [
      selectable,
      handleSelect,
      renderNode,
      checkable,
      classNames,
      styles,
      levelOffset,
      onExpand,
      setExpandedState,
      treeData,
    ],
  );

  return (
    <ScrollArea.Autosize
      mah={mah ?? '100%'}
      viewportRef={parentRef}
      scrollbars='y'
      classNames={{
        root: classNames?.root,
      }}
      style={styles?.root}
      {...scrollAreaProps}
    >
      <TreeIndicatorContainer
        value={value}
        indicators={Indicators}
        indicatorClasses={classNames?.indicator}
        style={{
          height: virtualizer.getTotalSize(),
          width: '100%',
          position: 'relative',
          ['--level-offset' as string]: rem(levelOffset),
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const item = flatList[virtualItem.index];
          if (!item) return null;

          return (
            <div
              key={item.node.value}
              ref={virtualizer.measureElement}
              data-index={virtualItem.index}
              data-testid={
                testIdPrefix
                  ? `${testIdPrefix}-row-${item.node.value}`
                  : `tree-row-${item.node.value}`
              }
              className={classes.virtualRow}
              style={{
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <MemoFlatTreeNode
                {...item}
                treeRef={treeRef}
                selected={value === item.node.value}
                expandOnClick={false}
                selectOnClick={false}
                expandOnSpace={false}
                renderNode={renderMantineNode}
                tabIndex={virtualItem.index === 0 ? 0 : -1}
                style={flatNodeStyle}
              />
            </div>
          );
        })}
      </TreeIndicatorContainer>
    </ScrollArea.Autosize>
  );
};